// AI-powered loan eligibility assessment service
import axios from 'axios';
import calculateCreditScore from './creditScoreCalculator';

class AIEligibilityService {
  constructor() {
    this.apiEndpoint = process.env.REACT_APP_AI_API_ENDPOINT || 'https://api.openai.com/v1/chat/completions';
    this.apiKey = process.env.REACT_APP_AI_API_KEY;
    this.model = process.env.REACT_APP_AI_MODEL || 'gpt-4o-mini';
  }

  /**
   * Analyzes borrower data and determines loan eligibility using AI
   * @param {Object} borrower - Complete borrower information
   * @param {Array} loans - Borrower's loan history
   * @param {Object} loanRequest - Requested loan details
   * @returns {Object} AI eligibility assessment
   */
  async assessEligibility(borrower, loans = [], loanRequest = {}) {
    try {
      // Calculate current credit score
      const creditScore = calculateCreditScore(borrower, loans);
      
      // Prepare borrower data for AI analysis
      const borrowerProfile = this.prepareBorrowerProfile(borrower, loans, creditScore);
      
      // Get AI assessment
      const aiAssessment = await this.getAIAssessment(borrowerProfile, loanRequest);
      
      // Combine AI insights with traditional scoring
      return this.combineAssessments(creditScore, aiAssessment, borrowerProfile, loanRequest);
      
    } catch (error) {
      console.error('AI Eligibility Assessment Error:', error);
      // Fallback to traditional assessment
      return this.fallbackAssessment(borrower, loans, loanRequest);
    }
  }

  /**
   * Prepares comprehensive borrower profile for AI analysis
   */
  prepareBorrowerProfile(borrower, loans, creditScore) {
    // Calculate key metrics
    const totalLoansAmount = loans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
    const totalPaidAmount = loans.reduce((sum, loan) => sum + (loan.paid_amount || 0), 0);
    const outstandingBalance = totalLoansAmount - totalPaidAmount;
    const paymentRatio = totalLoansAmount > 0 ? (totalPaidAmount / totalLoansAmount) * 100 : 0;
    
    // Count loans by status
    const activeLoans = loans.filter(loan => loan.loan_status === 7).length;
    const overdueLoans = loans.filter(loan => loan.loan_status === 8).length;
    const completedLoans = loans.filter(loan => loan.loan_status === 10).length;
    
    // Calculate average loan amount
    const avgLoanAmount = loans.length > 0 ? totalLoansAmount / loans.length : 0;
    
    // Employment stability indicator
    const employmentMonths = borrower.employer ? 
      this.calculateEmploymentMonths(borrower.date_created) : 0;
    
    return {
      personalInfo: {
        age: this.calculateAge(borrower.date_of_birth),
        employmentStatus: borrower.employer ? 'employed' : 'unemployed',
        employmentMonths,
        hasVerifiedPhone: !!borrower.phone,
        hasVerifiedNRC: !!borrower.nrc,
        hasNextOfKin: !!(borrower.next_kin_name && borrower.next_kin_phone)
      },
      financialProfile: {
        creditScore,
        totalLoansAmount,
        outstandingBalance,
        paymentRatio,
        activeLoans,
        overdueLoans,
        completedLoans,
        avgLoanAmount,
        loanHistory: loans.length
      },
      riskFactors: {
        hasOverdueLoans: overdueLoans > 0,
        multipleActiveLoans: activeLoans > 2,
        highUtilization: paymentRatio < 30,
        recentMember: employmentMonths < 6,
        inconsistentPayments: this.hasInconsistentPayments(loans)
      }
    };
  }

  /**
   * Gets AI assessment using language model
   */
  async getAIAssessment(borrowerProfile, loanRequest) {
    if (!this.apiKey) {
      throw new Error('AI API key not configured');
    }

    const prompt = this.buildAIPrompt(borrowerProfile, loanRequest);
    
    try {
      const response = await axios.post(
        this.apiEndpoint,
        {
          model: this.model,
          messages: [
            {
              role: "system",
              content: "You are a loan risk assessment expert. Analyze borrower data and provide detailed eligibility recommendations."
            },
            {
              role: "user",
              content: prompt
            }
          ],
          max_tokens: 1000,
          temperature: 0.3
        },
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return this.parseAIResponse(response.data.choices[0].message.content);
    } catch (error) {
      // If AI service fails, return structured fallback
      return this.getStructuredFallback(borrowerProfile, loanRequest);
    }
  }

  /**
   * Builds comprehensive prompt for AI analysis
   */
  buildAIPrompt(profile, loanRequest) {
    return `
Analyze this loan application and provide a detailed assessment:

BORROWER PROFILE:
- Credit Score: ${profile.financialProfile.creditScore}/850
- Total Loan History: ${profile.financialProfile.loanHistory} loans
- Payment Ratio: ${profile.financialProfile.paymentRatio.toFixed(1)}%
- Outstanding Balance: ZMW ${profile.financialProfile.outstandingBalance.toLocaleString()}
- Active Loans: ${profile.financialProfile.activeLoans}
- Overdue Loans: ${profile.financialProfile.overdueLoans}
- Employment: ${profile.personalInfo.employmentStatus} (${profile.personalInfo.employmentMonths} months)
- Verification: Phone(${profile.personalInfo.hasVerifiedPhone}), NRC(${profile.personalInfo.hasVerifiedNRC}), Next of Kin(${profile.personalInfo.hasNextOfKin})

RISK FACTORS:
- Has Overdue Loans: ${profile.riskFactors.hasOverdueLoans}
- Multiple Active Loans: ${profile.riskFactors.multipleActiveLoans}
- High Utilization: ${profile.riskFactors.highUtilization}
- Recent Member: ${profile.riskFactors.recentMember}
- Inconsistent Payments: ${profile.riskFactors.inconsistentPayments}

LOAN REQUEST:
- Amount: ZMW ${loanRequest.amount || 'Not specified'}
- Type: ${loanRequest.loan_type || 'Not specified'}
- Purpose: ${loanRequest.purpose || 'Not specified'}

Provide assessment in this JSON format:
{
  "recommendation": "APPROVE/CONDITIONAL_APPROVE/DECLINE",
  "confidence": 0-100,
  "riskLevel": "LOW/MEDIUM/HIGH",
  "maxLoanAmount": number,
  "suggestedInterestRate": number,
  "conditions": ["condition1", "condition2"],
  "riskFactors": ["factor1", "factor2"],
  "positiveFactors": ["factor1", "factor2"],
  "reasoning": "detailed explanation"
}
`;
  }

  /**
   * Parses AI response and ensures consistent format
   */
  parseAIResponse(aiContent) {
    try {
      // Try to extract JSON from AI response
      const jsonMatch = aiContent.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      
      // Fallback parsing for non-JSON responses
      return this.parseTextResponse(aiContent);
    } catch (error) {
      console.error('AI Response Parsing Error:', error);
      return this.getDefaultAssessment();
    }
  }

  /**
   * Combines AI assessment with traditional scoring
   */
  combineAssessments(creditScore, aiAssessment, profile, loanRequest) {
    // Calculate traditional recommendation
    const traditionalRecommendation = this.getTraditionalRecommendation(creditScore, profile);
    
    // Weight AI vs traditional (70% AI, 30% traditional for established borrowers)
    const aiWeight = profile.financialProfile.loanHistory >= 3 ? 0.7 : 0.4;
    const traditionalWeight = 1 - aiWeight;
    
    // Combine confidence scores
    const combinedConfidence = Math.round(
      (aiAssessment.confidence * aiWeight) + 
      (traditionalRecommendation.confidence * traditionalWeight)
    );
    
    // Determine final recommendation (most conservative wins)
    const recommendations = [aiAssessment.recommendation, traditionalRecommendation.recommendation];
    const finalRecommendation = this.getMostConservativeRecommendation(recommendations);
    
    return {
      ...aiAssessment,
      recommendation: finalRecommendation,
      confidence: combinedConfidence,
      traditionalScore: creditScore,
      aiInsights: aiAssessment.reasoning,
      combinedAnalysis: true,
      assessmentDetails: {
        aiWeight: Math.round(aiWeight * 100),
        traditionalWeight: Math.round(traditionalWeight * 100),
        aiRecommendation: aiAssessment.recommendation,
        traditionalRecommendation: traditionalRecommendation.recommendation
      }
    };
  }

  /**
   * Traditional recommendation based on credit score and basic rules
   */
  getTraditionalRecommendation(creditScore, profile) {
    let recommendation = 'DECLINE';
    let confidence = 60;
    let riskLevel = 'HIGH';
    
    if (creditScore >= 700 && profile.riskFactors.hasOverdueLoans === false) {
      recommendation = 'APPROVE';
      confidence = 85;
      riskLevel = 'LOW';
    } else if (creditScore >= 600 && profile.financialProfile.overdueLoans === 0) {
      recommendation = 'CONDITIONAL_APPROVE';
      confidence = 70;
      riskLevel = 'MEDIUM';
    }
    
    return { recommendation, confidence, riskLevel };
  }

  /**
   * Fallback assessment when AI is unavailable
   */
  fallbackAssessment(borrower, loans, loanRequest) {
    const creditScore = calculateCreditScore(borrower, loans);
    const profile = this.prepareBorrowerProfile(borrower, loans, creditScore);
    const traditional = this.getTraditionalRecommendation(creditScore, profile);
    
    return {
      ...traditional,
      maxLoanAmount: this.calculateMaxLoanAmount(creditScore, profile),
      suggestedInterestRate: this.calculateInterestRate(creditScore),
      conditions: this.getStandardConditions(profile),
      reasoning: 'Assessment based on traditional credit scoring (AI unavailable)',
      traditionalScore: creditScore,
      aiInsights: null,
      combinedAnalysis: false
    };
  }

  // Helper methods
  calculateAge(dateOfBirth) {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birth = new Date(dateOfBirth);
    return Math.floor((today - birth) / (365.25 * 24 * 60 * 60 * 1000));
  }

  calculateEmploymentMonths(dateCreated) {
    if (!dateCreated) return 0;
    const today = new Date();
    const created = new Date(dateCreated);
    return Math.floor((today - created) / (30 * 24 * 60 * 60 * 1000));
  }

  hasInconsistentPayments(loans) {
    const recentLoans = loans.filter(loan => 
      new Date() - new Date(loan.date_created) <= 365 * 24 * 60 * 60 * 1000
    );
    
    return recentLoans.some(loan => loan.loan_status === 8);
  }

  calculateMaxLoanAmount(creditScore, profile) {
    const baseAmount = 5000; // Base amount in ZMW
    const scoreMultiplier = creditScore / 300; // Scale based on credit score
    const historyBonus = Math.min(profile.financialProfile.completedLoans * 0.1, 0.5);
    
    return Math.round(baseAmount * scoreMultiplier * (1 + historyBonus));
  }

  calculateInterestRate(creditScore) {
    if (creditScore >= 750) return 15; // Excellent
    if (creditScore >= 700) return 18; // Very Good
    if (creditScore >= 650) return 22; // Good
    if (creditScore >= 600) return 26; // Fair
    return 30; // Poor
  }

  getStandardConditions(profile) {
    const conditions = [];
    
    if (!profile.personalInfo.hasVerifiedNRC) {
      conditions.push('NRC verification required');
    }
    
    if (!profile.personalInfo.hasNextOfKin) {
      conditions.push('Next of kin information required');
    }
    
    if (profile.riskFactors.multipleActiveLoans) {
      conditions.push('Limit to one active loan');
    }
    
    return conditions;
  }

  getMostConservativeRecommendation(recommendations) {
    if (recommendations.includes('DECLINE')) return 'DECLINE';
    if (recommendations.includes('CONDITIONAL_APPROVE')) return 'CONDITIONAL_APPROVE';
    return 'APPROVE';
  }

  getDefaultAssessment() {
    return {
      recommendation: 'CONDITIONAL_APPROVE',
      confidence: 60,
      riskLevel: 'MEDIUM',
      maxLoanAmount: 10000,
      suggestedInterestRate: 25,
      conditions: ['Standard verification required'],
      reasoning: 'Default assessment applied due to analysis error'
    };
  }

  getStructuredFallback(profile, loanRequest) {
    return {
      recommendation: profile.financialProfile.creditScore >= 650 ? 'CONDITIONAL_APPROVE' : 'DECLINE',
      confidence: 65,
      riskLevel: profile.riskFactors.hasOverdueLoans ? 'HIGH' : 'MEDIUM',
      maxLoanAmount: this.calculateMaxLoanAmount(profile.financialProfile.creditScore, profile),
      suggestedInterestRate: this.calculateInterestRate(profile.financialProfile.creditScore),
      conditions: this.getStandardConditions(profile),
      riskFactors: Object.keys(profile.riskFactors).filter(key => profile.riskFactors[key]),
      positiveFactors: this.getPositiveFactors(profile),
      reasoning: 'Assessment based on structured analysis (AI service unavailable)'
    };
  }

  getPositiveFactors(profile) {
    const factors = [];
    
    if (profile.financialProfile.creditScore >= 700) factors.push('Excellent credit score');
    if (profile.financialProfile.paymentRatio >= 80) factors.push('Strong payment history');
    if (profile.personalInfo.employmentMonths >= 12) factors.push('Stable employment');
    if (profile.financialProfile.completedLoans >= 3) factors.push('Proven loan completion');
    if (!profile.riskFactors.hasOverdueLoans) factors.push('No overdue loans');
    
    return factors;
  }

  parseTextResponse(content) {
    // Simple text parsing fallback
    const recommendation = content.includes('APPROVE') && !content.includes('DECLINE') ? 'APPROVE' :
                          content.includes('CONDITIONAL') ? 'CONDITIONAL_APPROVE' : 'DECLINE';
    
    return {
      recommendation,
      confidence: 70,
      riskLevel: 'MEDIUM',
      reasoning: content,
      maxLoanAmount: 15000,
      suggestedInterestRate: 25
    };
  }
}

export default AIEligibilityService;
